import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Landing page for magic-link activation. The Configurator sends the user here
 * after guest signup — we exchange the token for a JWT, store it via useAuth's
 * setter, then redirect to the requested `next` URL (typically
 * /client/moyen-de-paiement so the user finishes setting up SEPA/CB before the
 * mission auto-publishes).
 */
export default function MagicLinkVerify() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { loginWithToken } = useAuth();
    const [state, setState] = useState("verifying"); // verifying | success | error
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        const token = params.get("token");
        const next = params.get("next") || "/client";
        if (!token) {
            setState("error");
            setErrorMsg("Lien invalide — aucun token détecté.");
            return;
        }
        api.post("/auth/magic-link/verify", { token })
            .then(({ data }) => {
                loginWithToken(data.token, data.user);
                setState("success");
                setTimeout(() => nav(next, { replace: true }), 1200);
            })
            .catch((err) => {
                setState("error");
                setErrorMsg(
                    err?.response?.data?.detail ||
                    "Ce lien n'est plus valide (expiré, déjà utilisé, ou révoqué)."
                );
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center px-4"
             data-testid="magic-link-verify-page">
            <div className="max-w-md w-full sd-card p-8 text-center">
                {state === "verifying" && (
                    <>
                        <Loader2 className="inline animate-spin text-[#F5A623]" size={40} />
                        <h1 className="font-display text-xl mt-4 mb-2">Activation en cours…</h1>
                        <p className="text-sm text-slate-500">
                            Nous vérifions votre lien et préparons votre espace client.
                        </p>
                    </>
                )}
                {state === "success" && (
                    <>
                        <CheckCircle2 className="inline text-emerald-500" size={48} />
                        <h1 className="font-display text-xl mt-4 mb-2">Compte activé !</h1>
                        <p className="text-sm text-slate-500">
                            Redirection vers votre espace…
                        </p>
                    </>
                )}
                {state === "error" && (
                    <>
                        <AlertTriangle className="inline text-red-500" size={48} />
                        <h1 className="font-display text-xl mt-4 mb-2">Lien inutilisable</h1>
                        <p className="text-sm text-slate-500 mb-4">{errorMsg}</p>
                        <button
                            onClick={() => nav("/devis", { replace: true })}
                            className="btn-gold text-sm"
                            data-testid="magic-link-retry-btn"
                        >
                            Recommencer depuis mon devis
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
